import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toastr } from "react-redux-toastr";
import { State } from "../store/interfaces";
import {
  IAddLeadActivityModel,
  ILeadEvent,
  IUpdateEventModel,
  LeadService,
} from "../api";
import {
  getCompletedActivitiesAction,
  getCompletedActivitiesErrorAction,
  getCompletedActivitiesSuccessAction,
  getPlannedActivitiesAction,
  getPlannedActivitiesErrorAction,
  getPlannedActivitiesSuccessAction,
  setFilterInDoneActivities,
  getPlannedActivitiesAllAction,
  getCompletedActivitiesAllAction,
} from "../store/reducers/lead";
import { DateFormats, formatDate } from "../utils";
import { StatusCodes } from "http-status-codes";
import {
  ActivityModel,
  activityTypeToIconMapper,
} from "../../containers/leads/components/activities/add-activity";
import moment from "moment";
import { TimeReservationAPIModel } from "../../containers/leads/components/activities/time-reservation-form";

export const useLeadActivities = (leadService: LeadService) => {
  const leadState = useSelector((state: State) => state.lead);
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [updating, setUpdating] = useState<boolean>(false);

  const getAllLeadActivities = async (id: string, page?: number) => {
    const filter: any = leadState.doneActivities?.filter ?? "All";
    getPlannedActivities(id);
    getCompletedActivities(id, filter, page ?? 1);
  };

  const setFilter = (filter: string) => {
    dispatch(setFilterInDoneActivities({ filter }));
  };

  const getPlannedActivities = async (id: string, onSuccess?: () => void) => {
    dispatch(getPlannedActivitiesAction({ id }));
    try {
      const response = await leadService.getLeadActivities(id, "planned");
      if (response.status === StatusCodes.OK) {
        dispatch(getPlannedActivitiesSuccessAction({ data: response.data }));
      } else {
        dispatch(getPlannedActivitiesErrorAction());
      }
    } catch (err) {
      dispatch(getPlannedActivitiesErrorAction());
    }
  };

  const getCompletedActivities = async (
    id: string,
    filter: any,
    page?: number
  ) => {
    dispatch(getCompletedActivitiesAction({ id, page }));
    if (filter === "all") {
      filter = null;
    }
    try {
      const response = await leadService.getLeadActivities(id, "completed", {
        type: filter,
        page,
      });
      if (response.status === StatusCodes.OK) {
        dispatch(
          getCompletedActivitiesSuccessAction({
            data: response.data,
            page,
            totalCount: response.count,
          })
        );
      } else {
        dispatch(getCompletedActivitiesErrorAction());
      }
    } catch (err) {
      dispatch(getCompletedActivitiesErrorAction());
    }
  };

  const getLeadActivitiesAll = async (ids: string[], type?: string, customerId?: string) => {

    const filter: any = leadState.doneActivities?.filter ?? "all";
    getPlannedActivitiesAll(ids, undefined, type, customerId);
    getCompletedActivitiesAll(ids, filter, 1, type, customerId);
  };

  const getPlannedActivitiesAll = async (
    ids: string[],
    onSuccess?: () => void,
    type?: string,
    customerId?: string
  ) => {
    dispatch(getPlannedActivitiesAllAction({ ids }));
    try {
      const response = await leadService.getAllLeadActivities(
        ids,
        "planned",
        undefined,
        type,
        customerId
      );
      if (response.status === StatusCodes.OK) {
        dispatch(getPlannedActivitiesSuccessAction({ data: response.data }));
      } else {
        dispatch(getPlannedActivitiesErrorAction());
      }
    } catch (err) {
      dispatch(getPlannedActivitiesErrorAction());
    }
  };

  const getCompletedActivitiesAll = async (
    ids: string[],
    filter: any,
    page?: number,
    type?: string,
    customerId?: string,
  ) => {
    dispatch(getCompletedActivitiesAllAction({ ids, page }));
    if (filter === "all") {
      filter = null;
    }
    try {
      const response = await leadService.getAllLeadActivities(
        ids,
        "completed",
        {
          type: filter,
          page: page ?? 1,
        },
        type,
        customerId
      );
      if (response.status === StatusCodes.OK) {
        dispatch(
          getCompletedActivitiesSuccessAction({
            data: response.data,
            page,
            totalCount: response.count,
          })
        );
      } else {
        dispatch(getCompletedActivitiesErrorAction());
      }
    } catch (err) {
      dispatch(getCompletedActivitiesErrorAction());
    }
  };

  const addLeadActivity = async (
    data: IAddLeadActivityModel,
    onSuccess?: () => void,
    forAll?: boolean
  ) => {
    setUpdating(true);
    try {
      const response = await leadService.addLeadActivity(data);
      if (response.status === StatusCodes.OK) {
        if (!forAll) {
          if (data?.companyId) {
            getAllLeadActivities(data?.companyId);
          } else {
            getAllLeadActivities(data?.leadId!);
          }
        }

        if (onSuccess) {
          onSuccess();
        }
      } else {
        toastr.error("Error", response.message ?? "Unable to create activity");
      }
    } catch (error) {
      if(error.response?.data?.data?.description){
        toastr.error(
          "Error",
          error.response?.data?.data?.description 
        );
      }else if(typeof error.response?.data?.data?.message === 'string'){
        toastr.error(
          "Error",
          error.response?.data?.data?.message 
        );
      }else if(error.response?.data?.data?.message[0]){
        toastr.error(
          "Error",
          error.response?.data?.data?.message[0]
         );
      }else {
        toastr.error(
          "Error",
            "Something went wrong while creating activity"
        );
      }
    } finally {
      setUpdating(false);
    }
  };

  const createTimeReservation = async (
    data: TimeReservationAPIModel,
    onSuccess?: () => void
  ) => {
    setUpdating(true);
    try {
      const response = await leadService.addTimeReservation(data);
      if (response.status === StatusCodes.OK) {
        if (onSuccess) {
          onSuccess();
        }
      } else {
        toastr.error(
          "Error",
          response.message ?? "Unable to create time reservation"
        );
      }
    } catch (error) {
      toastr.error(
        "Error",
        error.response?.data?.data?.description ??
          error?.response?.data?.message ??
          "something went wrong while creating time reservation"
      );
    } finally {
      setUpdating(false);
    }
  };

  const createVehicleTimeReservation = async (
    data: TimeReservationAPIModel,
    onSuccess?: () => void
  ) => {
    setUpdating(true);
    try {
      const response = await leadService.addVehicleTimeReservation(data);
      if (response.status === StatusCodes.OK) {
        if (onSuccess) {
          onSuccess();
        }
      } else {
        toastr.error(
          "Error",
          response.message ?? "Unable to create time reservation"
        );
      }
    } catch (error) {
      toastr.error(
        "Error",
        error.response?.data?.data?.description ??
          error?.response?.data?.message ??
          "something went wrong while creating time reservation"
      );
    } finally {
      setUpdating(false);
    }
  };

  const getActivityIcon = (type: string, triggeredBy?: string) => {
    if (triggeredBy === "modifyQuality") {
      return "icon-mark-vsl";
    }
    const item = activityTypeToIconMapper[type];
    if (item != null) {
      return item.icon;
    }
    return "icon-lead";
  };

  const getEventDetails = async (
    eventId: string,
    onSuccess?: (event: ILeadEvent) => void
  ) => {
    setUpdating(true);
    try {
      const response = await leadService.getEventDetails(eventId);
      if (response.status === StatusCodes.OK) {
        if (onSuccess) {
          onSuccess(response.data);
        }
      } else {
        toastr.error(
          "Error",
          response.message ?? "Unable to fetch event details"
        );
      }
    } catch (err) {
      toastr.error(
        "Error",
        "something went wrong while fetching event details"
      );
    } finally {
      setUpdating(false);
    }
  };

  const activityMarkAsDone = async (
    leadId: string,
    eventId: string,
    onSuccess?: () => void,
    mileageIn?: string,
    mileageOut?: string
  ) => {
    setUpdating(true);
    try {
      const response = await leadService.markActivityAsDone(
        eventId,
        mileageIn,
        mileageOut
      );
      if (response.status === StatusCodes.OK) {
        toastr.success("Success", "Activity marked as done");
        if (onSuccess) {
          onSuccess();
        }
      } else {
        toastr.error("Error", response.message ?? "Unable to mark as done");
      }
    } catch (err) {
      toastr.error(
        "Error",
        (err as any).response?.data?.data?.message ||
          "Something went wrong while marking as done"
      );
    } finally {
      setUpdating(false);
    }
  };

  const activityMarkAsUnDone = async (
    leadId: string,
    eventId: string,
    onSuccess?: () => void
  ) => {
    setUpdating(true);
    try {
      const response = await leadService.markActivityAsUnDone(eventId);
      if (response.status === StatusCodes.OK) {
        toastr.success("Success", "Activity marked as un done");
        if (onSuccess) {
          onSuccess();
        }
      } else {
        toastr.error("Error", response.message ?? "Unable to mark as un done");
      }
    } catch (err) {
      toastr.error("Error", "Something went wrong while marking as un done");
    } finally {
      setUpdating(false);
    }
  };

  const createEventUpdateFormData = (eventDetails: ILeadEvent) => {
    const activityModel: ActivityModel = {
      type: eventDetails.activityType,
      title: eventDetails.title,
      eventId: eventDetails._id,
      timePeriod: {
        startDate: moment(eventDetails.startDate).endOf("day").toDate(),
        endDate: moment(eventDetails.endDate).endOf("day").toDate(),
        startTime: formatDate(
          eventDetails.startDate,
          DateFormats.TimeOnlyFormat
        ),
        endTime: formatDate(eventDetails.endDate, DateFormats.TimeOnlyFormat),
      },
      location: eventDetails?.testDriveLocation,
      salesPerson: {
        _id: eventDetails?.assignedPerson?._id ?? "",
        name: eventDetails?.assignedPerson?.name ?? "",
      },
      vehicleModel: eventDetails?.vehicleModel,
      vehicleVariant: eventDetails?.vehicleVariant,
      year: eventDetails?.year,
      vehicle: {
        _id: eventDetails?.vehicle?._id!,
        displayName: (eventDetails?.vehicle as any)?.name,
      },
      showRoom: {
        title: eventDetails?.showroom?.title,
        _id: eventDetails?.showroom?._id!,
      },
      busyEvent: eventDetails?.busyEvent,
      leadId: eventDetails.leadId,
      note: eventDetails.note,
      address: eventDetails.address,
      customer: eventDetails.customer,
      overnightTestdriveEnabled: eventDetails.overnightTestdriveEnabled,
      users: eventDetails.users,
      activityCategory: eventDetails.activityCategory,
      companyPeople: eventDetails.companyPeople,
      companyId: eventDetails.companyId, 
      activityCategoryId : eventDetails.propertyId || eventDetails.projectId
    };

    return activityModel;
  };

  const inititateTestDrive = async (
    eventId: string,
    onSuccess?: () => void
  ) => {
    setUpdating(true);
    try {
      const response = await leadService.inititateTestDrive(eventId);
      if (response.status === StatusCodes.OK) {
        toastr.success("Success", "Testdrive Initiated");
        if (onSuccess) {
          onSuccess();
        }
      } else {
        toastr.error(
          "Error",
          response.message ?? "Unable to initiate test drive"
        );
      }
    } catch (err) {
      toastr.error("Error", "Something went wrong while initiating test drive");
    } finally {
      setUpdating(false);
    }
  };

  const updateActivity = async (
    eventId: string,
    data: IUpdateEventModel,

    onSuccess?: () => void
  ) => {
    setUpdating(true);
    try {
      const response = await leadService.updateLeadActivity(eventId, data);
      if (response.status === StatusCodes.OK) {
        toastr.success("Success", "Activity updated");
        if (onSuccess) {
          onSuccess();
        }
      } else {
        toastr.error(
          "Error",
          response.message ?? "Unable to update the activity"
        );
      }
    } catch (err) {
      toastr.error(
        "Error",
        (err as any).response?.data?.data?.message ||
          "Something went wrong while updating activity"
      );
    } finally {
      setUpdating(false);
    }
  };

  return {
    leadState,
    isLoading,
    updating,
    getAllLeadActivities,
    getPlannedActivities,
    getCompletedActivities,
    getEventDetails,
    addLeadActivity,
    activityMarkAsDone,
    updateActivity,
    setFilter,
    getLeadActivitiesAll,
    createEventUpdateFormData,
    getActivityIcon,
    createTimeReservation,
    createVehicleTimeReservation,
    getCompletedActivitiesAll,
    activityMarkAsUnDone,
    inititateTestDrive,
  };
};
